/**
 * PHARMAKON — primitive components (shadcn-spirited, design-faithful port)
 *
 * Ported from the design's components/primitives/Primitives.jsx — single
 * consolidated module. Each individual primitive file (Button.tsx, Input.tsx,
 * etc.) re-exports the matching named component from here.
 *
 * Differences from the design source (documented in §7 deviation log):
 *   - Icon uses `lucide-react` ESM imports instead of the prototype's UMD
 *     `window.lucide` lookup. Same visual icons; standard React import.
 *   - Toast list comes from the existing ToastContext (scope answer #3 keeps
 *     existing real-app infra); the design's mock toastStore is not ported.
 */

import * as React from "react";
import { createPortal } from "react-dom";
import * as LucideIcons from "lucide-react";
import { cn } from "@/lib/utils";
import { ToastContext } from "@/context/toastContext.js";
import { useFocusTrap } from "@/hooks/useFocusTrap.js";
import { fmtMoney } from "@/config/locale.config.js";
import { LOADING_TEXT_DELAY_MS } from "@/config/ui.config.js";
import {
  normalizeTone,
  resolveStatus,
  statusIconFor,
} from "@/config/status.config.js";
import {
  isPrimaryEnter,
  isTopDialog,
  isTypedInput,
  PRIMARY_SELECTOR,
  pushDialog,
  removeDialog,
} from "@/lib/dialog.js";
import { nextTabIndex } from "@/lib/tabs.js";
import {
  normalizeToastType,
  resolveToastLifetime,
  toastRole,
  visibleToasts,
} from "@/lib/toast.js";

// ============================================================
// Icon — uses lucide-react. Accepts kebab-case OR PascalCase names.
// ============================================================
const toPascal = (s: string) =>
  s
    .split("-")
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join("");

export interface IconProps extends React.SVGAttributes<SVGSVGElement> {
  name: string;
  size?: number;
  strokeWidth?: number;
}
export const Icon: React.FC<IconProps> = ({
  name,
  size = 16,
  strokeWidth = 1.75,
  className = "",
  style = {},
  ...rest
}) => {
  if (!name) return null;
  const Comp = (LucideIcons as Record<string, React.ComponentType<any>>)[
    toPascal(name)
  ];
  if (!Comp) return null;
  return (
    <Comp
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      className={className}
      style={style}
      aria-hidden="true"
      {...rest}
    />
  );
};

// ============================================================
// Wordmark — PHARMAKON with accent dot on the I (design §LoginPage/AppShell)
// ============================================================
export interface WordmarkProps {
  size?: "sm" | "md" | "lg" | "xl";
  tone?: "default" | "inverted";
  className?: string;
}
export const Wordmark: React.FC<WordmarkProps> = ({
  size = "md",
  tone = "default",
  className = "",
}) => {
  const sizes = {
    sm: "text-md",
    md: "text-xl",
    lg: "text-3xl",
    xl: "text-4xl",
  } as const;
  return (
    <span
      className={cn(
        "font-sans font-bold tracking-tight inline-flex items-baseline gap-[0.08em]",
        sizes[size],
        tone === "inverted" ? "text-white" : "text-text-strong",
        className,
      )}
      style={{ letterSpacing: "-0.04em" }}
    >
      <span>PHARMAKON</span>
      <span
        aria-hidden="true"
        className="inline-block rounded-full bg-accent shrink-0"
        style={{
          width: "0.28em",
          height: "0.28em",
          alignSelf: "flex-end",
          marginBottom: "0.06em",
        }}
      ></span>
    </span>
  );
};

// ============================================================
// Button (ux/01 §3.1 — WP-15)
//
// Sizes: md 40 px (default) · lg 48 px (document / counter primary action) ·
// sm 32 px (grid rows and toolbars only; `xs` is kept as an alias of sm).
// `shortcut` renders the Kbd chip after the label and sets aria-keyshortcuts.
// Busy: when `onClick` returns a promise (or `loading` is set) the button shows
// a spinner and `busyLabel`, keeps its width, sets aria-busy and ignores clicks
// and keys until the promise settles — a double submission is impossible.
// `disabledReason`: the button stays focusable (aria-disabled) so the reason is
// read (aria-describedby) and shown on hover and focus.
//
// Keyboard: native button (Entrée / Espace). a11y: aria-busy, aria-disabled,
// aria-keyshortcuts, aria-describedby → reason.
// ============================================================
export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "danger-outline"
  | "link";
export type ButtonSize = "xs" | "sm" | "md" | "lg";
export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type" | "onClick"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  iconRight?: string;
  /** Externally driven busy state (e.g. a mutation's isPending). */
  loading?: boolean;
  /** Label while busy (« Validation… »); defaults to the normal label. */
  busyLabel?: React.ReactNode;
  /** Key chip after the label (« F9 », « Ctrl+S »). */
  shortcut?: string;
  /** Why the action is unavailable; implies disabled, keeps the button focusable. */
  disabledReason?: React.ReactNode;
  /** Full-width (the `.btn-block` modifier, AUD-UXDS-03). */
  block?: boolean;
  type?: "button" | "submit" | "reset";
  as?: React.ElementType;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => unknown;
  /** Router target when rendered `as={Link}`. */
  to?: string;
}
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      icon,
      iconRight,
      loading,
      busyLabel,
      shortcut,
      disabledReason,
      block,
      disabled,
      children,
      className = "",
      type = "button",
      as,
      onClick,
      style,
      ...rest
    },
    forwardedRef,
  ) => {
    const As: React.ElementType = as || "button";
    const innerRef = React.useRef<HTMLButtonElement | null>(null);
    const setRef = (node: HTMLButtonElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    };
    const [pending, setPending] = React.useState(false);
    const [lockedWidth, setLockedWidth] = React.useState<number | null>(null);
    const lockRef = React.useRef(false);
    const mounted = React.useRef(true);
    React.useEffect(() => {
      mounted.current = true;
      return () => {
        mounted.current = false;
      };
    }, []);
    const busy = Boolean(loading) || pending;
    const reasonId = React.useId();
    const softDisabled = Boolean(disabledReason) && !busy;
    const hardDisabled = Boolean(disabled) && !disabledReason;

    React.useEffect(() => {
      if (!busy) setLockedWidth(null);
    }, [busy]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (busy || softDisabled || lockRef.current) {
        e.preventDefault();
        return;
      }
      const result = onClick?.(e);
      if (result && typeof (result as Promise<unknown>).then === "function") {
        lockRef.current = true;
        const w = innerRef.current?.getBoundingClientRect?.().width;
        if (w) setLockedWidth(Math.ceil(w));
        setPending(true);
        (result as Promise<unknown>)
          .catch(() => {
            /* the caller reports its own error; the button only unlocks */
          })
          .finally(() => {
            lockRef.current = false;
            if (mounted.current) setPending(false);
          });
      }
    };

    // ERP v3 anatomy — the erp `.btn` classes (styles/global.css).
    const variantClass = {
      primary: "primary",
      secondary: "secondary",
      ghost: "ghost",
      danger: "danger",
      "danger-outline": "danger",
      link: "ghost",
    } as const;
    const sizeClass = { xs: "sm", sm: "sm", md: "", lg: "lg" } as const;
    const iconSize = size === "lg" ? 18 : size === "md" ? 18 : 16;
    const describedBy = [
      rest["aria-describedby"],
      disabledReason ? reasonId : undefined,
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <As
        ref={setRef}
        type={As === "button" ? type : undefined}
        disabled={hardDisabled || undefined}
        aria-disabled={softDisabled || busy || undefined}
        aria-busy={busy || undefined}
        aria-keyshortcuts={shortcut ? shortcut.replace(/\s+/g, "") : undefined}
        data-tip={disabledReason && typeof disabledReason === "string" ? disabledReason : undefined}
        className={cn(
          "btn",
          variantClass[variant],
          sizeClass[size],
          block && "btn-block",
          softDisabled && "is-disabled",
          busy && "is-busy",
          className,
        )}
        style={lockedWidth ? { ...style, minWidth: lockedWidth } : style}
        {...rest}
        aria-describedby={describedBy || undefined}
        onClick={handleClick}
      >
        {busy ? (
          <Icon name="loader-2" size={iconSize} className="animate-spin" />
        ) : (
          icon && <Icon name={icon} size={iconSize} />
        )}
        {busy && busyLabel ? busyLabel : children}
        {iconRight && !busy && <Icon name={iconRight} size={iconSize} />}
        {shortcut && <Kbd className="pk-btn-kbd">{shortcut}</Kbd>}
        {disabledReason && (
          <span id={reasonId} className="sr-only">
            {disabledReason}
          </span>
        )}
      </As>
    );
  },
);
Button.displayName = "Button";

// ============================================================
// IconButton — icon-only, always labelled (ux/01 §3.1)
// 40×40 (md), 32×32 (sm, grids), 48×48 (lg). The label is the accessible
// name, the native hover tooltip and a CSS tooltip on keyboard focus.
// ============================================================
export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  label: string;
  size?: "sm" | "md" | "lg";
  variant?: "ghost" | "secondary" | "primary";
  shortcut?: string;
}
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { icon, label, size = "md", variant = "ghost", shortcut, className = "", ...rest },
    ref,
  ) => {
    const variantClass = {
      ghost: "ghost",
      secondary: "secondary",
      primary: "primary",
    } as const;
    const sizeClass = { sm: "sm", md: "", lg: "lg" } as const;
    const tip = shortcut ? `${label} (${shortcut})` : label;
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        aria-keyshortcuts={shortcut ? shortcut.replace(/\s+/g, "") : undefined}
        title={tip}
        data-tip={tip}
        className={cn(
          "btn icon pk-tip",
          variantClass[variant],
          sizeClass[size],
          className,
        )}
        {...rest}
      >
        <Icon name={icon} size={size === "lg" ? 20 : size === "sm" ? 16 : 18} />
      </button>
    );
  },
);
IconButton.displayName = "IconButton";

// ============================================================
// Field context — ties a Label, its control, the help text and the error
// together (ids, aria-describedby, aria-invalid, aria-required). Controls
// that report their own parse error (NumberInput, DateInput…) push it here.
// ============================================================
export interface FieldContextValue {
  id: string;
  /** The first control rendered in the Field owns the id (no duplicate ids). */
  claim: React.MutableRefObject<symbol | null>;
  /**
   * The claims of controls that are mounted (committed). A claim taken by a render React discarded —
   * the first of StrictMode's two renders in development — is never in it, so the kept control takes
   * the id back at commit and the label stays associated (X XWP-55, EXE-71).
   */
  live: Set<symbol>;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
  reportError: (message: string | null) => void;
}
export const FieldContext = React.createContext<FieldContextValue | null>(null);
export const useField = () => React.useContext(FieldContext);

/** Layout effect in the browser (the id is fixed before paint), effect elsewhere (server render). */
const useFieldCommitEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

/** Merge the Field wiring into a control's own props (explicit props win). */
function useFieldControlProps(
  own: { id?: string; "aria-describedby"?: string; "aria-invalid"?: unknown; required?: boolean },
  error?: boolean | string,
) {
  const field = useField();
  const token = React.useRef<symbol>(Symbol("field-control")).current;
  if (field && field.claim.current == null) field.claim.current = token;
  const owns = Boolean(field) && field!.claim.current === token;
  const ownsRef = React.useRef(owns);
  ownsRef.current = owns;
  const [, rerender] = React.useReducer((n: number) => n + 1, 0);
  useFieldCommitEffect(() => {
    if (!field) return undefined;
    // the claim of a discarded render (StrictMode) or of an unmounted control is free again
    const libre = field.claim.current == null || !field.live.has(field.claim.current);
    if (libre) field.claim.current = token;
    if (field.claim.current === token) {
      field.live.add(token);
      if (!ownsRef.current) rerender(); // the render that committed did not carry the id yet
    }
    return () => {
      field.live.delete(token);
      if (field.claim.current === token) field.claim.current = null;
    };
  }, [field, token]);
  const describedBy = [own["aria-describedby"], field?.describedBy]
    .filter(Boolean)
    .join(" ");
  const invalid = Boolean(error) || Boolean(field?.invalid) || own["aria-invalid"] === true;
  return {
    id: own.id ?? (owns ? field!.id : undefined),
    "aria-describedby": describedBy || undefined,
    "aria-invalid": invalid || undefined,
    "aria-required": (own.required ?? field?.required) || undefined,
  };
}

// ============================================================
// Input / Textarea / Select (ux/01 §3.2)
// 40 px (md) · 48 px (lg) · 32 px (sm, in-grid). States: focus/editing (CSS),
// read-only, `computed` (derived value: read-only, dashed border, calculator
// icon), `overridden` (3 px warning bar; the « Modifié » chip and the reset
// action are drawn by Field `overridden`), error (aria-invalid), disabled.
// ============================================================
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  icon?: string;
  suffix?: React.ReactNode;
  error?: boolean | string;
  size?: "sm" | "md" | "lg";
  /** Derived value (Line HT, À commander): read-only with the calculator mark. */
  computed?: boolean;
  /** The user replaced a computed value. */
  overridden?: boolean;
  /** Class on the <input> itself (className goes on the wrapper). */
  inputClassName?: string;
}
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      icon,
      suffix,
      error,
      className = "",
      size = "md",
      computed,
      overridden,
      inputClassName,
      readOnly,
      ...rest
    },
    ref,
  ) => {
    const sizes = {
      sm: "h-8",
      md: "h-10",
      lg: "h-12",
    } as const;
    const lead = computed ? "calculator" : icon;
    const iconPadding = {
      sm: "pl-8",
      md: "pl-9",
      lg: "pl-10",
    } as const;
    const wiring = useFieldControlProps(rest as never, error);
    return (
      <div className={cn("relative inline-flex items-center w-full", className)}>
        {lead && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
            <Icon name={lead} size={16} />
          </span>
        )}
        <input
          ref={ref}
          // ERP v3 anatomy — the erp `.input` (Novera focus ring, editing fill).
          className={cn(
            "input",
            sizes[size],
            lead ? iconPadding[size] : "",
            suffix ? "pr-12" : "",
            error && "err",
            computed && "pk-computed",
            overridden && "pk-overridden",
            inputClassName,
          )}
          readOnly={computed || readOnly}
          {...rest}
          {...wiring}
        />
        {suffix && (
          <span className="pk-affix absolute right-3 text-text-muted pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean | string;
}
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ error, className = "", rows = 4, ...rest }, ref) => {
    const wiring = useFieldControlProps(rest as never, error);
    return (
      // ERP v3 anatomy — the erp `.textarea`.
      <textarea
        ref={ref}
        rows={rows}
        className={cn("textarea", error && "err", className)}
        {...rest}
        {...wiring}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export interface SelectOption {
  value: string | number;
  label: string;
}
export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  options?: Array<SelectOption | string | number>;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  error?: boolean | string;
}
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ options = [], placeholder, className = "", size = "md", error, ...rest }, ref) => {
    const sizes = {
      sm: "h-8 text-sm",
      md: "h-10 text-base",
      lg: "h-12 text-base",
    } as const;
    const wiring = useFieldControlProps(rest as never, error);
    return (
      <div className={cn("relative inline-flex items-center w-full", className)}>
        <select
          ref={ref}
          // ERP v3 anatomy — the erp `.selectbox`.
          className={cn("selectbox pr-9", sizes[size], error && "err")}
          {...rest}
          {...wiring}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((o) => {
            const opt: SelectOption =
              typeof o === "object" ? o : { value: o, label: String(o) };
            return (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            );
          })}
        </select>
        <Icon
          name="chevron-down"
          size={16}
          className="absolute right-3 text-text-muted pointer-events-none"
        />
      </div>
    );
  },
);
Select.displayName = "Select";

// ============================================================
// Label & Field (ux/01 §3.2 anatomy)
// Label above the field, 14 px/600 muted; « * » paired with the visually
// hidden « (obligatoire) »; help text below (14 px); the error replaces the
// help text (danger ink + alert-circle), linked by aria-describedby, and the
// control gets aria-invalid. A control's own parse error (NumberInput,
// DateInput…) is shown the same way unless the caller passes `error`.
// ============================================================
export interface LabelProps
  extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
}
export const Label: React.FC<LabelProps> = ({
  children,
  required,
  htmlFor,
  className = "",
  ...rest
}) => (
  <label htmlFor={htmlFor} className={cn("pk-label", className)} {...rest}>
    {children}
    {required && (
      <>
        <span className="pk-label-req" aria-hidden="true">
          {" "}
          *
        </span>
        <span className="sr-only"> (obligatoire)</span>
      </>
    )}
  </label>
);

export interface FieldProps {
  label?: React.ReactNode;
  required?: boolean;
  error?: string | null;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** id given to the control (generated when omitted). */
  id?: string;
  /** The value was overridden by the user: « Modifié » chip + reset action. */
  overridden?: boolean;
  /** « Rétablir la valeur calculée ». */
  onReset?: () => void;
  /** Warning shown next to an overridden value (§D3). */
  overrideWarning?: React.ReactNode;
}
export const Field: React.FC<FieldProps> = ({
  label,
  required,
  error,
  hint,
  children,
  className = "",
  id,
  overridden,
  onReset,
  overrideWarning,
}) => {
  const autoId = React.useId();
  const controlId = id ?? `f${autoId.replace(/:/g, "")}`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-err`;
  const [ownError, setOwnError] = React.useState<string | null>(null);
  const claim = React.useRef<symbol | null>(null);
  const live = React.useRef<Set<symbol>>(new Set()).current;
  const shownError = error || ownError;
  const describedBy = shownError ? errorId : hint ? hintId : undefined;
  const ctx = React.useMemo<FieldContextValue>(
    () => ({
      id: controlId,
      claim,
      live,
      describedBy,
      invalid: Boolean(shownError),
      required: Boolean(required),
      reportError: setOwnError,
    }),
    [controlId, describedBy, shownError, required, live],
  );
  return (
    <div className={cn("pk-field block", className)}>
      {label && (
        <Label htmlFor={controlId} required={required}>
          {label}
        </Label>
      )}
      <FieldContext.Provider value={ctx}>{children}</FieldContext.Provider>
      {overridden && (
        <div className="pk-field-override">
          <Badge tone="warning" icon="pencil">
            Modifié
          </Badge>
          {onReset && (
            <button type="button" className="pk-link" onClick={onReset}>
              Rétablir la valeur calculée
            </button>
          )}
          {overrideWarning && <span className="pk-field-help">{overrideWarning}</span>}
        </div>
      )}
      {hint && !shownError && (
        <p id={hintId} className="pk-field-help">
          {hint}
        </p>
      )}
      {shownError && (
        <p id={errorId} className="pk-field-error">
          <Icon name="alert-circle" size={16} />
          <span>{shownError}</span>
        </p>
      )}
    </div>
  );
};

// ============================================================
// Badge — status chip on the ONE registry (ux/01 §2.3, §3.10)
//
// 24 px, 13 px/600, sentence case. Tones: neutral · info · warning · success ·
// danger · theorique (legacy `accent` = success). Success, warning, danger and
// théorique draw a leading 14 px icon (shape redundancy, P-05); `status`
// resolves word + tone + icon from config/status.config.js so a status looks
// the same on every screen. `solid` is reserved for one strong state per
// screen (the document status in the page header). Not interactive.
// ============================================================
export type BadgeTone =
  | "neutral"
  | "accent"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "theorique";
export interface BadgeProps {
  tone?: BadgeTone;
  /** Registered status: "facture_achat.VALIDE" (config/status.config.js). */
  status?: string;
  children?: React.ReactNode;
  /** Explicit lucide icon; `false` suppresses the tone icon. */
  icon?: string | false;
  /** Legacy status dot (suppresses the tone icon). */
  dot?: boolean;
  solid?: boolean;
  /** Draw the label struck through (Annulé). */
  strike?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  title?: string;
}
export const Badge: React.FC<BadgeProps> = ({
  tone = "neutral",
  status,
  children,
  icon,
  dot,
  solid,
  strike,
  className = "",
  title,
}) => {
  const resolved = status ? resolveStatus(status) : null;
  const t = normalizeTone(resolved?.tone ?? tone);
  const glyph =
    icon === false || dot
      ? null
      : icon || resolved?.icon || statusIconFor(t);
  const label = children ?? resolved?.label ?? (status ? status.split(".").pop() : null);
  return (
    <span
      className={cn(
        "pk-badge",
        `pk-badge--${t}`,
        solid && "pk-badge--solid",
        (strike || resolved?.strike) && "pk-badge--strike",
        className,
      )}
      title={title}
    >
      {dot && <span className="pk-badge-dot" aria-hidden="true"></span>}
      {glyph && <Icon name={glyph} size={14} />}
      <span className="pk-badge-label">{label}</span>
    </span>
  );
};

// ============================================================
// Card
// ============================================================
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: "none" | "sm" | "md" | "lg";
}
export const Card: React.FC<CardProps> = ({
  children,
  padding = "md",
  className = "",
  ...rest
}) => {
  // ERP v3 anatomy — the erp `.panel` surface (hard 4px border, no soft shadow).
  const p = { none: "", sm: "p-3", md: "p-[var(--card-pad)]", lg: "p-6" }[
    padding
  ];
  return (
    <div className={cn("panel", p, className)} {...rest}>
      {children}
    </div>
  );
};

export interface CardHeaderProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}
export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  className = "",
}) => (
  <div className={cn("flex items-start justify-between gap-3 mb-4", className)}>
    <div className="min-w-0">
      {title && (
        <h3 className="text-md font-bold text-text-strong truncate">
          {title}
        </h3>
      )}
      {subtitle && <p className="text-xs text-text-muted mt-0.5">{subtitle}</p>}
    </div>
    {action && <div className="flex items-center gap-2 shrink-0">{action}</div>}
  </div>
);

// ============================================================
// Dialog behaviour shared by Modal and Drawer (ux/01 §3.6, §3.7, §4.5)
//
//  · initial focus: initialFocusRef → [data-autofocus] → [autofocus] → first
//    field → primary action; NEVER the × (AUD-UXDS-07, AUD-RECL-08);
//  · focus trap, focus returned to the opener (useFocusTrap);
//  · Échap closes the TOP dialog only; with typed data it first asks
//    « Abandonner la saisie ? » (Continuer la saisie focused / Abandonner);
//  · the backdrop never closes a dialog holding typed data;
//  · Entrée in a single-line field (outside a form, grid or combobox)
//    triggers the footer's primary action;
//  · body scroll locked while open.
// `dirty` forces the guard on/off; left undefined, the dialog becomes dirty as
// soon as the user types in one of its text fields.
// ============================================================
function useDialogBehaviour({
  open,
  onClose,
  dirty,
  initialFocusRef,
  closeOnBackdrop,
  onPrimaryKey,
}: {
  open: boolean;
  onClose?: () => void;
  dirty?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
  closeOnBackdrop: boolean;
  onPrimaryKey?: string;
}) {
  const panelRef = useFocusTrap(open, undefined, {
    initialFocusRef,
  }) as React.RefObject<HTMLDivElement>;
  const titleId = React.useId();
  const dialogId = React.useId();
  const [typed, setTyped] = React.useState(false);
  const [guardOpen, setGuardOpen] = React.useState(false);
  const isDirty = dirty ?? typed;
  const closeRef = React.useRef(onClose);
  const dirtyRef = React.useRef(isDirty);
  React.useEffect(() => {
    closeRef.current = onClose;
    dirtyRef.current = isDirty;
  });

  React.useEffect(() => {
    if (!open) {
      setTyped(false);
      setGuardOpen(false);
    }
  }, [open]);

  const requestClose = React.useCallback(() => {
    if (!closeRef.current) return;
    if (dirtyRef.current) setGuardOpen(true);
    else closeRef.current();
  }, []);

  React.useEffect(() => {
    if (!open) return;
    pushDialog(dialogId);
    const onKey = (e: KeyboardEvent) => {
      if (!isTopDialog(dialogId)) return;
      if (e.key === "Escape") {
        // A popup inside the dialog (combobox list, date picker) that handled
        // Échap first marks the event; the dialog stays open.
        if (e.defaultPrevented) return;
        e.preventDefault();
        requestClose();
        return;
      }
      const panel = panelRef.current;
      if (!panel || !(e.target instanceof Node) || !panel.contains(e.target)) return;
      const primaryHit =
        (onPrimaryKey && e.key === onPrimaryKey) || isPrimaryEnter(e);
      if (primaryHit) {
        const primary = panel.querySelector<HTMLElement>(PRIMARY_SELECTOR);
        if (primary && primary.getAttribute("aria-disabled") !== "true") {
          e.preventDefault();
          primary.click();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      removeDialog(dialogId);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, dialogId, requestClose, panelRef, onPrimaryKey]);

  const onInput = (e: React.FormEvent<HTMLDivElement>) => {
    // React bubbles the input of a dialog stacked over this one (portaled, but a React descendant) to
    // here: only a field of THIS panel makes it « typed » (X XWP-55, EXE-86).
    const target = e.target as HTMLElement;
    if (!typed && panelRef.current?.contains(target) && isTypedInput(target)) setTyped(true);
  };
  const onBackdrop = () => {
    if (!closeOnBackdrop || dirtyRef.current) return;
    closeRef.current?.();
  };
  const guard = (
    <ConfirmDialog
      open={guardOpen}
      onClose={() => setGuardOpen(false)}
      onConfirm={() => {
        // Close the guard first so its trap hands focus back inside the
        // dialog, then close the dialog on the next task: focus returns to
        // the dialog's opener, not to a detached field.
        setGuardOpen(false);
        setTimeout(() => closeRef.current?.(), 0);
      }}
      title="Abandonner la saisie ?"
      message="Les informations saisies dans cette fenêtre seront perdues."
      confirmLabel="Abandonner"
      cancelLabel="Continuer la saisie"
      destructive
    />
  );
  return { panelRef, titleId, requestClose, onInput, onBackdrop, guard };
}

/** The × of a dialog or drawer: last in DOM order, drawn at the top right. */
const DialogClose: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div className="pk-dialog-close" data-dialog-close="">
    <IconButton icon="x" label="Fermer (Échap)" onClick={onClick} />
  </div>
);

// ============================================================
// Modal (ux/01 §3.7)
// Sizes sm/md/lg = 480/640/880 px (`xl` = lg), capped at 100vw − 32 px and
// 100vh − 64 px. Title 20 px/600, 16 px body, footer right-aligned: secondary
// actions, then « Annuler », then the primary action (with its key chip).
// Keyboard: Tab cycles inside · Échap closes (guarded) · Entrée → primary.
// a11y: role="dialog", aria-modal, aria-labelledby → title.
// ============================================================
export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  closeOnBackdrop?: boolean;
  /** Explicit dirty state; undefined = dirty once the user types. */
  dirty?: boolean;
  /** Explicit initial focus target (wins over the §4.5 order). */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /** A key (e.g. "F9") that also triggers the primary action. */
  primaryKey?: string;
  className?: string;
}
const DIALOG_WIDTH = {
  sm: "var(--pk-dialog-sm)",
  md: "var(--pk-dialog-md)",
  lg: "var(--pk-dialog-lg)",
  xl: "var(--pk-dialog-lg)",
} as const;
export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "md",
  closeOnBackdrop = true,
  dirty,
  initialFocusRef,
  primaryKey,
  className = "",
}) => {
  const { panelRef, titleId, requestClose, onInput, onBackdrop, guard } =
    useDialogBehaviour({
      open,
      onClose,
      dirty,
      initialFocusRef,
      closeOnBackdrop,
      onPrimaryKey: primaryKey,
    });
  if (!open) return null;
  // Portaled to <body>: keeps the fixed overlay out of any transformed
  // ancestor (which would re-anchor `position:fixed`).
  return createPortal(
    <div className="pk-overlay pk-overlay--dialog">
      <div className="pk-scrim" onClick={onBackdrop}></div>
      <div
        ref={panelRef}
        className={cn("modal-panel pk-dialog", className)}
        style={{
          width: `min(${DIALOG_WIDTH[size]}, calc(100vw - 32px))`,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onInput={onInput}
      >
        {(title || subtitle) && (
          <div className="pk-dialog-head">
            {title && (
              <h2 id={titleId} className="pk-dialog-title">
                {title}
              </h2>
            )}
            {subtitle && <p className="pk-dialog-sub">{subtitle}</p>}
          </div>
        )}
        <div className="pk-dialog-body">{children}</div>
        {footer && (
          <div className="pk-dialog-foot" data-dialog-footer="">
            {footer}
          </div>
        )}
        {onClose && <DialogClose onClick={requestClose} />}
      </div>
      {guard}
    </div>,
    document.body,
  );
};

// ============================================================
// Drawer — modal overlay from the right (ux/01 §3.6)
// Widths sm/md/lg = 400/560/720 px, capped at 100vw − 48 px. Header: title
// 20 px/600 + optional subtitle; body scrolls vertically; sticky footer with
// the actions; the × is last in DOM order, labelled « Fermer (Échap) ».
// Same keyboard and focus rules as Modal.
// ============================================================
export interface DrawerProps {
  open: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  width?: "sm" | "md" | "lg";
  dirty?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
  closeOnBackdrop?: boolean;
  primaryKey?: string;
  className?: string;
}
export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = "md",
  dirty,
  initialFocusRef,
  closeOnBackdrop = true,
  primaryKey,
  className = "",
}) => {
  const { panelRef, titleId, requestClose, onInput, onBackdrop, guard } =
    useDialogBehaviour({
      open,
      onClose,
      dirty,
      initialFocusRef,
      closeOnBackdrop,
      onPrimaryKey: primaryKey,
    });
  if (!open) return null;
  return createPortal(
    <div className="pk-overlay pk-overlay--drawer">
      <div className="pk-scrim" onClick={onBackdrop}></div>
      <div
        ref={panelRef}
        className={cn("drawer-panel pk-drawer", className)}
        style={{ width: `min(var(--pk-drawer-${width}), calc(100vw - 48px))` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onInput={onInput}
      >
        <div className="pk-dialog-head">
          {title && (
            <h2 id={titleId} className="pk-dialog-title">
              {title}
            </h2>
          )}
          {subtitle && <p className="pk-dialog-sub">{subtitle}</p>}
        </div>
        <div className="pk-drawer-body">{children}</div>
        {footer && (
          <div className="pk-dialog-foot pk-drawer-foot" data-dialog-footer="">
            {footer}
          </div>
        )}
        {onClose && <DialogClose onClick={requestClose} />}
      </div>
      {guard}
    </div>,
    document.body,
  );
};

// ============================================================
// Tabs (ux/01 §3.14)
// One Tab stop (roving tabindex); ← / → move AND activate (automatic
// activation), Début / Fin first / last, disabled tabs skipped. The strip
// wraps its own overflow (horizontal scroll inside the strip, never the page —
// EXE-40). The active tab is URL-addressable with `useUrlTab` (?tab=<id>).
// Pass `idBase` and render `TabPanel`s with the same `idBase` to link each tab
// to its panel (aria-controls / aria-labelledby).
// ============================================================
export interface TabItem {
  value: string;
  label: React.ReactNode;
  icon?: string;
  count?: number;
  disabled?: boolean;
}
export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange?: (v: string) => void;
  className?: string;
  /** Accessible name of the tab list. */
  label?: string;
  /** Id prefix shared with the TabPanels. */
  idBase?: string;
}
export const tabId = (base: string, value: string) => `${base}-tab-${value}`;
export const tabPanelId = (base: string, value: string) => `${base}-panel-${value}`;
export const Tabs: React.FC<TabsProps> = ({
  items,
  value,
  onChange,
  className = "",
  label,
  idBase,
}) => {
  const autoBase = React.useId();
  const base = idBase ?? autoBase;
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = items.findIndex((it) => it.value === value);
  const focusIndex = activeIndex >= 0 ? activeIndex : items.findIndex((it) => !it.disabled);
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const next = nextTabIndex(
      items.map((it) => !it.disabled),
      focusIndex,
      e.key,
    );
    if (next == null) return;
    e.preventDefault();
    refs.current[next]?.focus();
    onChange?.(items[next].value);
  };
  return (
    // ERP v3 anatomy — the erp `.tabs`/`.tab.active` (accent underline + .tcount).
    <div
      className={cn("tabs pk-tabs", className)}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
    >
      {items.map((it, i) => {
        const active = it.value === value;
        return (
          <button
            key={it.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={tabId(base, it.value)}
            aria-selected={active}
            aria-controls={idBase ? tabPanelId(base, it.value) : undefined}
            aria-disabled={it.disabled || undefined}
            tabIndex={i === focusIndex ? 0 : -1}
            onClick={() => !it.disabled && onChange?.(it.value)}
            className={cn("tab", active && "active")}
          >
            {it.icon && <Icon name={it.icon} size={16} />}
            {it.label}
            {it.count != null && <span className="tcount">{it.count}</span>}
          </button>
        );
      })}
    </div>
  );
};

export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  idBase: string;
  value: string;
  /** The currently selected tab: the panel renders only when it matches. */
  selected: string;
}
export const TabPanel: React.FC<TabPanelProps> = ({
  idBase,
  value,
  selected,
  children,
  ...rest
}) =>
  value === selected ? (
    <div
      role="tabpanel"
      id={tabPanelId(idBase, value)}
      aria-labelledby={tabId(idBase, value)}
      tabIndex={0}
      {...rest}
    >
      {children}
    </div>
  ) : null;

// ============================================================
// Table
// ============================================================
export const Table: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className = "",
}) => (
  // ERP v3 anatomy — the erp `.tbl` inside a bordered panel wrapper.
  <div className={cn("tbl-wrap panel", className)}>
    <table className="tbl">{children}</table>
  </div>
);

export interface TheadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  sticky?: boolean;
}
export const Thead: React.FC<TheadProps> = ({
  children,
  sticky,
  className = "",
  ...rest
}) => (
  // erp `.tbl thead th` styles the header cells (uppercase, muted, surf-up bg).
  <thead className={cn(sticky && "sticky top-0 z-10", className)} {...rest}>
    {children}
  </thead>
);
export const Tbody: React.FC<React.PropsWithChildren> = ({ children }) => (
  <tbody>{children}</tbody>
);
export interface TrProps extends React.HTMLAttributes<HTMLTableRowElement> {}
export const Tr: React.FC<TrProps> = ({
  children,
  onClick,
  className = "",
  ...rest
}) => {
  // B2G A11Y-4 — a clickable row is a keyboard target (EG-13, ported from
  // web-medecin): tabIndex=0 + Enter/Space activation + the row focus keyline
  // (.tbl tbody tr[tabindex]:focus-visible in global.css). Guarded on
  // e.target so keys pressed on in-row controls (IconButton, links) are never
  // hijacked; rows without onClick render exactly as before (no tab stop).
  // Callers may still override via their own tabIndex/onKeyDown in ...rest.
  const keyboard: React.HTMLAttributes<HTMLTableRowElement> = onClick
    ? {
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            (onClick as unknown as (ev: React.SyntheticEvent) => void)(e);
          }
        },
      }
    : {};
  return (
    // erp `.tbl tbody tr` adds the hover tint + accent ::before bar; `.sel` selects.
    <tr onClick={onClick} className={className} {...keyboard} {...rest}>
      {children}
    </tr>
  );
};
export type TableDensity = "compact" | "comfortable";
export interface ThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sorted?: "asc" | "desc" | null;
  density?: TableDensity;
  numeric?: boolean;
}
export const Th: React.FC<ThProps> = ({
  children,
  align = "left",
  className = "",
  sortable,
  sorted,
  density: _density,
  numeric,
  onClick,
  style,
  ...rest
}) => {
  const effectiveAlign = numeric ? "right" : align;
  // ux/01 §3.5 — a sortable header is a real <button> inside the <th>, which
  // carries aria-sort; the button is natively focusable and activates on
  // Entrée / Espace (B2G A11Y-4). A non-sortable header forwards its handlers
  // and attributes as given (WP-16: Td/Th no longer drop them).
  const sortButton = sortable && onClick;
  return (
    <th
      aria-sort={
        sortable
          ? sorted === "asc"
            ? "ascending"
            : sorted === "desc"
              ? "descending"
              : "none"
          : undefined
      }
      onClick={sortButton ? undefined : onClick}
      {...rest}
      // erp `.tbl thead th` styles padding/uppercase/bg; `.num` right-aligns.
      className={cn(
        numeric && "num",
        sortable && "sortable",
        sorted && "sorted",
        className,
      )}
      style={
        effectiveAlign !== "left" ? { textAlign: effectiveAlign, ...style } : style
      }
    >
      {sortButton ? (
        <button
          type="button"
          className="sh pk-grid-sort"
          onClick={onClick as unknown as React.MouseEventHandler<HTMLButtonElement>}
        >
          {children}
          <Icon
            name={
              sorted === "desc"
                ? "arrow-down"
                : sorted === "asc"
                  ? "arrow-up"
                  : "chevrons-up-down"
            }
            size={16}
            className="pk-grid-sort-icon"
            aria-hidden="true"
          />
        </button>
      ) : (
        <span className="sh">
          {children}
          {sortable && (
            <Icon
              name={
                sorted === "desc"
                  ? "arrow-down"
                  : sorted === "asc"
                    ? "arrow-up"
                    : "chevrons-up-down"
              }
              size={16}
              aria-hidden="true"
            />
          )}
        </span>
      )}
    </th>
  );
};
export interface TdProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  mono?: boolean;
  numeric?: boolean;
  density?: TableDensity;
}
export const Td: React.FC<TdProps> = ({
  children,
  align = "left",
  mono,
  numeric,
  density: _density,
  className = "",
  style,
  ...rest
}) => {
  const effectiveAlign = numeric ? "right" : align;
  return (
    <td
      // WP-16: onClick (the stopPropagation of in-row action cells), colSpan,
      // title, aria-* … are forwarded — they were silently dropped before.
      {...rest}
      // erp `.tbl td.num` = right-aligned mono; `.mono` = mono font.
      className={cn(numeric && "num", mono && !numeric && "mono", className)}
      style={
        effectiveAlign !== "left" ? { textAlign: effectiveAlign, ...style } : style
      }
    >
      {children}
    </td>
  );
};

// ============================================================
// Skeleton, LoadingState, EmptyState, ErrorState (ux/01 §3.12)
//
//  · LoadingState: static skeleton rows (no shimmer under reduced motion);
//    « Chargement… » appears after LOADING_TEXT_DELAY_MS (400 ms); zeros are
//    never shown before the data arrives. aria-busy + a polite live region.
//  · EmptyState: 32 px icon, one sentence, the primary action; with active
//    filters, « Aucun résultat avec ces filtres » + « Réinitialiser les filtres ».
//  · ErrorState: what failed and what to do, « Réessayer » (focused when
//    focus is not already somewhere the user put it), a 403 says the role is
//    not allowed, « Détails techniques » collapsible (code + request id, never
//    a stack trace).
// ============================================================
export interface SkeletonProps {
  w?: string | number;
  h?: string | number;
  className?: string;
}
export const Skeleton: React.FC<SkeletonProps> = ({
  w = "100%",
  h = 16,
  className = "",
}) => (
  <div
    className={cn("skeleton", className)}
    style={{ width: w, height: h }}
    aria-hidden="true"
  ></div>
);

/** True once `delayMs` has elapsed while `active` stays true. */
export function useDelayedFlag(active: boolean, delayMs: number): boolean {
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    if (!active) {
      setShown(false);
      return undefined;
    }
    const t = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(t);
  }, [active, delayMs]);
  return shown;
}

export interface LoadingStateProps {
  /** Skeleton rows (grid) or blocks. */
  rows?: number;
  /** Column widths of a grid skeleton, e.g. ["40%", 120, 96]. */
  columns?: Array<string | number>;
  /** "rows" (grid body) · "block" (a card / KPI) · "form". */
  variant?: "rows" | "block" | "form";
  /** Accessible text, also shown after the delay. */
  label?: string;
  className?: string;
}
export const LoadingState: React.FC<LoadingStateProps> = ({
  rows = 6,
  columns = ["40%", "18%", "14%", "12%"],
  variant = "rows",
  label = "Chargement…",
  className = "",
}) => {
  const showText = useDelayedFlag(true, LOADING_TEXT_DELAY_MS);
  return (
    <div className={cn("pk-state pk-state--loading", className)} aria-busy="true">
      {variant === "rows" && (
        <div className="pk-skel-rows">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="pk-skel-row">
              {columns.map((w, j) => (
                <Skeleton key={j} w={w} h={14} />
              ))}
            </div>
          ))}
        </div>
      )}
      {variant === "block" && <Skeleton h={rows * 24} />}
      {variant === "form" && (
        <div className="pk-skel-form">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i}>
              <Skeleton w={112} h={14} />
              <Skeleton h={40} />
            </div>
          ))}
        </div>
      )}
      <p className={cn("pk-state-loading-text", !showText && "sr-only")} role="status">
        {label}
      </p>
    </div>
  );
};

export interface EmptyStateProps {
  icon?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** Filters are active: the standard filtered sentence and reset action. */
  filtered?: boolean;
  onResetFilters?: () => void;
  className?: string;
}
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon = "inbox",
  title,
  description,
  action,
  filtered,
  onResetFilters,
  className = "",
}) => (
  <div className={cn("pk-state pk-state--empty", className)}>
    <Icon name={filtered ? "filter-x" : icon} size={32} className="pk-state-icon" />
    {filtered ? (
      <>
        <p className="pk-state-title">Aucun résultat avec ces filtres.</p>
        {onResetFilters && (
          <Button variant="secondary" icon="rotate-ccw" onClick={onResetFilters}>
            Réinitialiser les filtres
          </Button>
        )}
      </>
    ) : (
      <>
        {title && <p className="pk-state-title">{title}</p>}
        {description && <p className="pk-state-text">{description}</p>}
        {action}
      </>
    )}
  </div>
);

/** What an error means for the user (plain French) and its technical trace. */
export interface DescribedError {
  title: string;
  message: string;
  status?: number;
  code?: string;
  requestId?: string;
}
export function describeError(err: unknown): DescribedError {
  const e = err as any;
  const status: number | undefined =
    e?.response?.status ?? e?.statusCode ?? e?.status ?? undefined;
  // The API client rejects with the response BODY (`services/api.client.js`: `{ success: false,
  // message, code, details }`), an axios error only when there is no body (network) — both read the
  // server's own French message (ux/03 §0.6, AUD-AVOIR-11).
  const body =
    e?.response?.data ??
    (e && typeof e === "object" && e.success === false && !(e instanceof Error) ? e : undefined);
  const code: string | undefined = body?.code ?? e?.code ?? undefined;
  const requestId: string | undefined =
    e?.response?.headers?.["x-request-id"] ??
    body?.requestId ??
    e?.requestId ??
    e?.traceId ??
    undefined;
  if (status === 403) {
    return {
      title: "Accès non autorisé",
      message:
        "Votre rôle ne permet pas d'accéder à ces informations. Demandez au titulaire de la pharmacie si vous en avez besoin.",
      status,
      code,
      requestId,
    };
  }
  if (status === 404) {
    return {
      title: "Élément introuvable",
      message: "Il a peut-être été supprimé ou déplacé.",
      status,
      code,
      requestId,
    };
  }
  if (!status && (e?.code === "ERR_NETWORK" || e?.message === "Network Error")) {
    return {
      title: "Serveur injoignable",
      message: "Vérifiez la connexion réseau puis réessayez.",
      code: e?.code,
      requestId,
    };
  }
  const serverMessage =
    typeof body?.message === "string" && body.message.trim() ? body.message : null;
  return {
    title: "Le chargement a échoué",
    message:
      serverMessage ??
      (typeof err === "string" ? err : "Une erreur est survenue. Réessayez dans un instant."),
    status,
    code,
    requestId,
  };
}

export interface ErrorStateProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** The caught error: drives the message, the 403 wording and the details. */
  error?: unknown;
  retry?: () => void;
  className?: string;
}
export const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  description,
  error,
  retry,
  className = "",
}) => {
  const d = error != null ? describeError(error) : null;
  const retryRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    // Focus « Réessayer » unless the user is already working somewhere
    // (async results never steal focus, §4.5).
    const a = document.activeElement;
    if (!a || a === document.body) retryRef.current?.focus();
  }, []);
  const details = d && (d.code || d.requestId || d.status);
  return (
    <div className={cn("pk-state pk-state--error", className)} role="alert">
      <Icon name="alert-circle" size={32} className="pk-state-icon pk-state-icon--danger" />
      <p className="pk-state-title">{title ?? d?.title ?? "Une erreur est survenue"}</p>
      {(description ?? d?.message) && (
        <p className="pk-state-text">{description ?? d?.message}</p>
      )}
      {retry && (
        <Button ref={retryRef} variant="secondary" icon="rotate-cw" onClick={retry}>
          Réessayer
        </Button>
      )}
      {details && (
        <details className="pk-state-details">
          <summary>Détails techniques</summary>
          <dl>
            {d!.status != null && (
              <>
                <dt>Statut</dt>
                <dd className="font-mono">{d!.status}</dd>
              </>
            )}
            {d!.code && (
              <>
                <dt>Code</dt>
                <dd className="font-mono">{d!.code}</dd>
              </>
            )}
            {d!.requestId && (
              <>
                <dt>Requête</dt>
                <dd className="font-mono">{d!.requestId}</dd>
              </>
            )}
          </dl>
        </details>
      )}
    </div>
  );
};

// ============================================================
// MoneyText — the one money display (ux/01 §3.2, OP-F-04)
// fmtMoney output, tabular numerals, right-aligned in grids, a leading « − »
// and danger ink for negatives, `signed` for écarts (« +73,42 DA »), the
// théorique style (D-06), « — » for an unknown value (never « 0,00 » while
// loading). `noSuffix` inside a grid whose header carries « (DA) ».
// ============================================================
export interface MoneyTextProps {
  value: unknown;
  signed?: boolean;
  noSuffix?: boolean;
  theorique?: boolean;
  /** Negative values in danger ink (default true). */
  negativeInk?: boolean;
  strong?: boolean;
  className?: string;
}
export const MoneyText: React.FC<MoneyTextProps> = ({
  value,
  signed,
  noSuffix,
  theorique,
  negativeInk = true,
  strong,
  className = "",
}) => {
  const text = fmtMoney(value as never, { signed, noSuffix });
  const unknown = text === "—";
  const negative = !unknown && text.startsWith("\u2212");
  return (
    <span
      className={cn(
        "pk-money",
        negative && negativeInk && "pk-money--negative",
        theorique && "pk-money--theorique",
        strong && "font-bold",
        className,
      )}
      aria-label={unknown ? "Montant inconnu" : undefined}
    >
      {theorique && <Icon name="hourglass" size={14} />}
      {text}
    </span>
  );
};

// ============================================================
// Tooltip — hover and focus, Échap dismisses (WCAG 1.4.13)
// ============================================================
export interface TooltipProps {
  label: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "right";
  /** Make the wrapper a tab stop (for tooltips around disabled controls). */
  focusable?: boolean;
  /** Rich content (a note preview…): wraps at 320 px instead of one line. */
  multiline?: boolean;
}
/**
 * Tooltip / preview bubble (ux/01 §3.0: never the only carrier of
 * information; shown on hover AND focus). WCAG 1.4.13: Échap dismisses it
 * without moving focus (and without closing an enclosing dialog); it comes
 * back on the next hover or focus. The bubble describes the wrapped control
 * (aria-describedby on the wrapper's focus target is the caller's job when
 * the content is essential — use `multiline` previews as a supplement).
 */
export const Tooltip: React.FC<TooltipProps> = ({
  label,
  children,
  side = "top",
  focusable = false,
  multiline = false,
}) => {
  const [dismissed, setDismissed] = React.useState(false);
  const id = React.useId();
  return (
    // A-07 — the bubble shows on hover OR focus-within. `focusable` makes the
    // wrapper itself a tab stop for the disabled-control case.
    <span
      className="relative inline-flex group"
      tabIndex={focusable ? 0 : undefined}
      aria-describedby={focusable ? id : undefined}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !dismissed) {
          e.preventDefault();
          e.stopPropagation();
          setDismissed(true);
        }
      }}
      onMouseLeave={() => setDismissed(false)}
      onBlur={() => setDismissed(false)}
    >
      {children}
      <span
        id={id}
        className={cn(
          "pointer-events-none absolute opacity-0 transition-opacity duration-fast",
          !dismissed && "group-hover:opacity-100 group-focus-within:opacity-100",
          "text-sm px-2 py-1 rounded",
          multiline ? "w-max max-w-[320px] whitespace-normal text-left" : "whitespace-nowrap",
          side === "top" && "bottom-full mb-1.5 left-1/2 -translate-x-1/2",
          side === "bottom" && "top-full mt-1.5 left-1/2 -translate-x-1/2",
          side === "right" && "left-full ml-1.5 top-1/2 -translate-y-1/2",
        )}
        role="tooltip"
        style={{
          background: "var(--pk-text-strong)",
          color: "var(--pk-surface)",
          zIndex: "var(--pk-z-tooltip)" as unknown as number,
        }}
      >
        {label}
      </span>
    </span>
  );
};

// ============================================================
// Kbd — key chip (ux/01 §3.1, §4.6): 13 px/600 in a 24 px chip.
// Decorative for AT when the owner already carries aria-keyshortcuts.
// ============================================================
export const Kbd: React.FC<React.HTMLAttributes<HTMLElement>> = ({
  children,
  className = "",
  ...rest
}) => (
  <kbd className={cn("kbd pk-kbd", className)} {...rest}>
    {children}
  </kbd>
);

// ============================================================
// Checkbox (ux/01 §3.15): 20 px box, 40 px hit area (the whole label is
// clickable), control border 3:1, accent fill + white check, `indeterminate`
// draws a bar. Keyboard: Espace toggles (native).
// ============================================================
export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  indeterminate?: boolean;
}
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, hint, checked, onChange, indeterminate, className = "", ...rest }, ref) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    React.useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = Boolean(indeterminate);
    }, [indeterminate]);
    const setRef = (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };
    return (
      <label className={cn("pk-check", className)}>
        <input
          ref={setRef}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          aria-checked={indeterminate ? "mixed" : undefined}
          className="cbx pk-check-box"
          {...rest}
        />
        {(label || hint) && (
          <span className="pk-check-text">
            {label && <span className="pk-check-label">{label}</span>}
            {hint && <span className="pk-check-hint">{hint}</span>}
          </span>
        )}
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";

// ============================================================
// Toggle — only for settings that take effect immediately (ux/01 §3.15);
// everywhere else a Checkbox. role="switch", Espace toggles, 40 px hit area.
// ============================================================
export interface ToggleProps {
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  id?: string;
}
export const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  label,
  disabled,
  className = "",
  id,
}) => (
  <label className={cn("pk-toggle", disabled && "is-disabled", className)}>
    <input
      id={id}
      type="checkbox"
      role="switch"
      checked={Boolean(checked)}
      aria-checked={Boolean(checked)}
      onChange={onChange}
      disabled={disabled}
      className="pk-toggle-input"
    />
    <span className="pk-toggle-track" aria-hidden="true">
      <span className="pk-toggle-thumb"></span>
    </span>
    {label && <span className="pk-toggle-label">{label}</span>}
  </label>
);

// ============================================================
// ToastHost — the ONE toast host, mounted at App level (ux/01 §3.9,
// AUD-UXDS-09). Bottom right, above the action bar, at most 3 stacked.
// success 4 s / info 5 s (role="status"), warning 8 s / error persistent
// (role="alert"); hovering or focusing a toast pauses its timer; a 32 px
// dismiss button « Fermer la notification »; an optional action link.
// A toast never takes focus and is never used for validation errors.
// ============================================================
interface ToastItemData {
  id: string;
  message: React.ReactNode;
  type?: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
}
const TOAST_ICON: Record<string, string> = {
  success: "check-circle-2",
  info: "info",
  warning: "alert-triangle",
  error: "x-circle",
  loading: "loader-2",
};
const ToastItem: React.FC<{ toast: ToastItemData; onDismiss: (id: string) => void }> = ({
  toast,
  onDismiss,
}) => {
  const type = normalizeToastType(toast.type);
  const lifetime = resolveToastLifetime(type, toast.duration);
  const [paused, setPaused] = React.useState(false);
  const remaining = React.useRef(lifetime);
  const startedAt = React.useRef(0);
  React.useEffect(() => {
    if (lifetime === 0 || paused) return undefined;
    startedAt.current = Date.now();
    const t = setTimeout(() => onDismiss(toast.id), remaining.current);
    return () => {
      clearTimeout(t);
      remaining.current = Math.max(
        1000,
        remaining.current - (Date.now() - startedAt.current),
      );
    };
  }, [lifetime, paused, onDismiss, toast.id]);
  const tone = type === "loading" ? "info" : type === "error" ? "danger" : type;
  return (
    <div
      role={toastRole(type)}
      className={cn("pk-toast", `pk-toast--${tone}`)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Icon
        name={TOAST_ICON[type]}
        size={20}
        className={cn("pk-toast-icon", type === "loading" && "animate-spin")}
      />
      <div className="pk-toast-body">
        <span className="pk-toast-msg">{toast.message}</span>
        {toast.action && (
          <button
            type="button"
            className="pk-link"
            onClick={() => {
              toast.action!.onClick();
              onDismiss(toast.id);
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <IconButton
        icon="x"
        label="Fermer la notification"
        size="sm"
        onClick={() => onDismiss(toast.id)}
      />
    </div>
  );
};
export const ToastHost: React.FC = () => {
  const ctx = React.useContext(ToastContext) as
    | { toasts?: ToastItemData[]; removeToast?: (id: string) => void }
    | undefined;
  const remove = ctx?.removeToast;
  const onDismiss = React.useCallback((id: string) => remove?.(id), [remove]);
  const shown = visibleToasts(ctx?.toasts ?? []) as ToastItemData[];
  return (
    <div className="pk-toast-host" aria-label="Notifications">
      {shown.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

// ============================================================
// Pagination
// ============================================================
export interface PaginationProps {
  page?: number;
  total?: number;
  onChange?: (p: number) => void;
}
export const Pagination: React.FC<PaginationProps> = ({
  page = 1,
  total = 1,
  onChange,
}) => (
  <div className="flex items-center justify-between gap-3 text-xs text-text-muted">
    <span>
      Page <span className="font-medium text-text-default">{page}</span> sur{" "}
      <span className="font-medium text-text-default">{total}</span>
    </span>
    <div className="flex items-center gap-1">
      <IconButton
        icon="chevrons-left"
        label="Première"
        size="sm"
        onClick={() => onChange?.(1)}
        disabled={page <= 1}
      />
      <IconButton
        icon="chevron-left"
        label="Précédente"
        size="sm"
        onClick={() => onChange?.(page - 1)}
        disabled={page <= 1}
      />
      <IconButton
        icon="chevron-right"
        label="Suivante"
        size="sm"
        onClick={() => onChange?.(page + 1)}
        disabled={page >= total}
      />
      <IconButton
        icon="chevrons-right"
        label="Dernière"
        size="sm"
        onClick={() => onChange?.(total)}
        disabled={page >= total}
      />
    </div>
  </div>
);

// ============================================================
// ConfirmDialog (ux/01 §3.8)
// The title is a question with a verb (« Supprimer la ligne 3 ? »), the body
// gives the consequence, buttons carry explicit verbs. Destructive: danger
// button, initial focus on « Annuler ». Financial commit (`summary`): a summary
// block (fournisseur, N°, TTC, lignes, écart), the primary action focused and
// bound to F9 and Entrée (« F9 → F9 »). `onConfirm` may return a promise: the
// primary button is busy until it settles; the dialog closes on success and
// stays open on failure (the caller reports the error).
// Keyboard: Tab cycles · Échap = Annuler · Entrée on the focused button ·
// F9 = confirm when `summary` / `shortcut` is set.
// ============================================================
export interface ConfirmSummaryRow {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Right-align numbers and money. */
  numeric?: boolean;
}
export interface ConfirmDialogProps {
  open: boolean;
  onClose?: () => void;
  onConfirm?: () => unknown;
  title?: React.ReactNode;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  requireTyping?: string;
  /** Financial commit summary rows; enables the F9 → F9 flow. */
  summary?: ConfirmSummaryRow[];
  /** Key bound to the primary action (default « F9 » with a summary). */
  shortcut?: string;
  busyLabel?: React.ReactNode;
}
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  destructive,
  requireTyping,
  summary,
  shortcut,
  busyLabel,
}) => {
  const [typed, setTyped] = React.useState("");
  React.useEffect(() => {
    if (!open) setTyped("");
  }, [open]);
  const ok = !requireTyping || typed === requireTyping;
  const key = shortcut ?? (summary && summary.length > 0 ? "F9" : undefined);
  const focusCancel = destructive && !requireTyping;
  const focusPrimary = !destructive && !requireTyping;
  const confirm = async () => {
    try {
      await onConfirm?.();
      onClose?.();
    } catch {
      // Stay open: the caller surfaced the failure (toast or inline error).
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size={summary ? "md" : "sm"}
      dirty={false}
      closeOnBackdrop={!requireTyping}
      primaryKey={key}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            {...(focusCancel ? { "data-autofocus": "" } : {})}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            disabledReason={ok ? undefined : `Tapez « ${requireTyping} » pour confirmer`}
            shortcut={key}
            busyLabel={busyLabel}
            onClick={confirm}
            data-dialog-primary=""
            {...(focusPrimary ? { "data-autofocus": "" } : {})}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <div className="pk-confirm-message">{message}</div>}
      {summary && summary.length > 0 && (
        <dl className="pk-confirm-summary">
          {summary.map((row, i) => (
            <React.Fragment key={i}>
              <dt>{row.label}</dt>
              <dd className={cn(row.numeric && "num")}>{row.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {requireTyping && (
        <Field
          className="mt-4"
          label={
            <>
              Tapez <span className="font-mono font-semibold">{requireTyping}</span> pour
              confirmer
            </>
          }
        >
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={requireTyping}
            autoComplete="off"
          />
        </Field>
      )}
    </Modal>
  );
};

// ============================================================
// DS-v2 FORM LAYER — FormSection / SummaryRail / SummaryCard /
// SummaryStat / CompletionRing / InputAffix
//
// Ported from the new design system form layer (ds/form.css:
// .fsection, .srail, .srail-stat, .cring, .input-affix). Tailwind +
// DS tokens only, dark-mode aware, density-aware via the --card-pad
// CSS var. No autosuggest component is added — the live ProduitForm
// suggestion bar is kept verbatim.
// ============================================================

// --- FormSection: numbered-step section card (.fsection) ---
export interface FormSectionProps {
  step?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}
export const FormSection: React.FC<FormSectionProps> = ({
  step,
  title,
  subtitle,
  action,
  children,
  className = "",
}) => (
  // ERP v3 `.fsec` — 3px accent top border, border over shadow.
  <section
    className={cn(
      "bg-surface border border-border-subtle border-t-[3px] border-t-accent rounded mb-5 overflow-visible",
      className,
    )}
  >
    <div className="flex items-center gap-3 px-5 py-4 border-b border-border-subtle">
      {step != null && (
        <span className="w-[30px] h-[30px] rounded-full bg-accent text-accent-fg font-mono font-bold text-sm flex items-center justify-center shrink-0">
          {step}
        </span>
      )}
      <div className="min-w-0">
        {title && (
          <h3 className="text-base font-[650] text-text-strong">{title}</h3>
        )}
        {subtitle && (
          <p className="text-xs text-text-muted mt-0.5">{subtitle}</p>
        )}
      </div>
      {action && <div className="ml-auto flex items-center gap-2 shrink-0">{action}</div>}
    </div>
    <div className="p-[var(--card-pad)]">{children}</div>
  </section>
);

// --- SummaryRail: sticky aside column (.srail) ---
export const SummaryRail: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className = "" }) => (
  <aside className={cn("sticky top-4 flex flex-col gap-4", className)}>
    {children}
  </aside>
);

// --- SummaryCard: card shell for rail contents (.srail-card) ---
export const SummaryCard: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className = "" }) => (
  <div
    className={cn(
      "bg-surface border border-border-subtle rounded-lg shadow-card overflow-hidden",
      className,
    )}
  >
    {children}
  </div>
);

// --- SummaryStat: key/value row (.srail-stat) ---
// Value may be passed via `value` or as children; `value` wins.
export interface SummaryStatProps {
  icon?: string;
  label?: React.ReactNode;
  value?: React.ReactNode;
  accent?: boolean;
  children?: React.ReactNode;
  className?: string;
}
export const SummaryStat: React.FC<SummaryStatProps> = ({
  icon,
  label,
  value,
  accent,
  children,
  className = "",
}) => (
  <div
    className={cn(
      "flex items-center justify-between px-5 py-[13px] border-t border-border-subtle",
      className,
    )}
  >
    <span className="flex items-center gap-1.5 text-xs font-[550] text-text-muted">
      {icon && <Icon name={icon} size={14} />}
      {label}
    </span>
    <span
      className={cn(
        "font-mono tabular-nums font-semibold text-base text-text-strong",
        accent && "text-accent",
      )}
    >
      {value != null ? value : children}
    </span>
  </div>
);

// --- CompletionRing: conic-gradient completion donut (.cring) ---
// The conic-gradient uses CSS-var tokens (var(--color-accent) /
// var(--color-bg-sunken)) — the single allowed style attribute, not hex.
export interface CompletionRingProps {
  percent?: number;
  label?: React.ReactNode;
  nudge?: React.ReactNode;
  className?: string;
}
export const CompletionRing: React.FC<CompletionRingProps> = ({
  percent = 0,
  label,
  nudge,
  className = "",
}) => {
  const p = Math.max(0, Math.min(100, Math.round(percent)));
  const ring = (
    <div className="relative w-[52px] h-[52px] shrink-0">
      <div
        className="w-[52px] h-[52px] rounded-full"
        style={{
          background: `conic-gradient(var(--color-accent) ${p}%, var(--color-bg-sunken) 0)`,
        }}
      />
      <div className="absolute inset-[7px] rounded-full bg-surface" />
      <span className="absolute inset-0 grid place-items-center font-mono text-[11px] font-bold text-text-strong">
        {p}%
      </span>
    </div>
  );
  if (!label && !nudge) {
    return className ? <div className={className}>{ring}</div> : ring;
  }
  return (
    <div className={cn("flex items-center gap-3.5", className)}>
      {ring}
      <div className="min-w-0">
        {label && (
          <p className="text-sm font-semibold text-text-strong">{label}</p>
        )}
        {nudge && <p className="text-xs text-text-muted mt-0.5">{nudge}</p>}
      </div>
    </div>
  );
};

// --- InputAffix: pins a fixed unit suffix to the right of an <Input> ---
// Caller passes the existing <Input> as child and adds
// className="text-right pr-11 font-mono" to it.
export interface InputAffixProps {
  suffix?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}
export const InputAffix: React.FC<InputAffixProps> = ({
  suffix,
  children,
  className = "",
}) => (
  <div className={cn("relative flex items-center", className)}>
    {children}
    {suffix != null && (
      <span className="absolute right-3 font-mono text-sm font-semibold text-text-muted pointer-events-none">
        {suffix}
      </span>
    )}
  </div>
);
